import React, { useEffect, useState, useRef, Fragment, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ShoppingCart, Trash2, Plus, Minus, ArrowRight, ArrowLeft, CreditCard, Banknote, FileText, MapPin, Phone, Mail, Clock, MessageSquare, CheckCircle2, CalendarDays, Loader2, Info, ChevronLeft, ChevronRight, Lock, Check, Truck, ClipboardCheck, Send, Upload, Image as ImageIcon } from 'lucide-react';
import { useCart } from './CartContext';
import { useNavigate } from 'react-router-dom';
import { sendFormEmail } from '../utils/sendFormEmail';
import { Skeleton } from './Skeleton';
import { getZoneForPosition } from '../data/deliveryZones';
import { useT } from '../utils/i18n';
import { saveOrderForTracking, buildShareLink } from '../utils/orderTracking';
// Format a 10/11-digit phone string into (XXX) XXX-XXXX
function formatPhone(raw: string): string {
  if (!raw) return '';
  const digits = raw.replace(/\D/g, '');
  const ten = digits.length === 11 && digits.startsWith('1') ? digits.slice(1) : digits;
  if (ten.length !== 10) return raw;
  return `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}`;
}
function getDensity(productName: string): number {
  const name = productName.toLowerCase();
  if (name.includes('bark') || name.includes('play chips')) return 0.45;
  if (name.includes('compost')) return 0.6;
  if (name.includes('sand')) return 1.4;
  return 1.3;
}
function isLightMaterial(name: string): boolean {
  const n = name.toLowerCase();
  return n.includes('bark') || n.includes('compost') || n.includes('topsoil') || n.includes('50/50') || n.includes('fill dirt') || n.includes('play chips');
}
// Single source of truth: shared polygon zones used by the Delivery Zones map page.
function getZoneInfo(lat: number, lon: number) {
  const result = getZoneForPosition(lat, lon);
  return {
    zone: result.zone > 0 ? result.zone : null,
    fee: result.fee
  };
}
type DrawerView = 'cart' | 'checkout' | 'success';
export function CartDrawer() {
  const {
    items,
    isCartOpen,
    closeCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    loadSavedCart,
    totalPrice,
    totalYards,
    deliveryCount,
    deliveryAddress,
    deliveryFee,
    deliveryZone,
    isCalculatingDelivery,
    setDeliveryInfo,
    setIsCalculatingDelivery,
    clearDeliveryInfo,
    promo,
    applyPromoCode,
    clearPromoCode
  } = useCart();
  const [promoInput, setPromoInput] = useState('');
  const [showPromoInput, setShowPromoInput] = useState(false);
  // Auto-prefill promo input if user copied FIRST10 from the PromoBar
  useEffect(() => {
    if (isCartOpen && !promo.code) {
      try {
        const hint = sessionStorage.getItem('promo_hint');
        if (hint) {
          setPromoInput(hint);
          setShowPromoInput(true);
        }
      } catch {}
    }
  }, [isCartOpen, promo.code]);
  const navigate = useNavigate();
  const [view, setView] = useState<DrawerView>('cart');
  // Checkout form state
  const [addressInput, setAddressInput] = useState(deliveryAddress);
  const [addressError, setAddressError] = useState('');
  // Hydrate the local address input from context whenever the cart opens
  // (e.g. when an estimator flow pre-fills the address via setDeliveryInfo).
  // Only hydrate if the user hasn't typed something else in the drawer yet,
  // so we never stomp an in-progress edit.
  useEffect(() => {
    if (isCartOpen && deliveryAddress && !addressInput) {
      setAddressInput(deliveryAddress);
    }
  }, [isCartOpen, deliveryAddress]);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [deliveryTime, setDeliveryTime] = useState('');
  const [instructions, setInstructions] = useState('');
  const [dumpLocation, setDumpLocation] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);
  // If a previously chosen time slot becomes invalid for the selected day
  // (e.g. user picks Afternoon, then switches to a Saturday — Sat ends 1 PM
  // so afternoon is closed), clear it so they re-select.
  useEffect(() => {
    if (!deliveryDate || !deliveryTime) return;
    const dow = new Date(deliveryDate + 'T12:00:00').getDay();
    if (dow === 0) {
      setDeliveryTime('');
    } else if (dow === 6 && deliveryTime === 'afternoon') {
      setDeliveryTime('');
    }
  }, [deliveryDate, deliveryTime]);
  const [calendarMonth, setCalendarMonth] = useState(new Date());
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'delivery'>('delivery');
  const [payOnDeliverySubMethod, setPayOnDeliverySubMethod] = useState<'cash' | 'card'>('cash');
  const [cardName, setCardName] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [orderSummaryData, setOrderSummaryData] = useState<any>(null);
  // New features state
  const [sitePhoto, setSitePhoto] = useState<File | null>(null);
  const [sitePhotoPreview, setSitePhotoPreview] = useState<string | null>(null);
  const [smsOptIn, setSmsOptIn] = useState(true);
  const totalDeliveryFee = deliveryFee * deliveryCount;
  // Material yards = what the customer actually needs (uses requestedQuantity
  // when the last load was rounded up to the 5yd minimum). Billed yards
  // (totalYards from context) is what we charge for. When they differ, we
  // surface both so the customer knows the extra 1yd is a billing minimum,
  // not extra material going to their site.
  const materialYards = items.reduce((sum, item) => sum + (item.requestedQuantity ?? item.quantity), 0);
  const yardsBilledExtra = totalYards - materialYards;
  // WA combined sales tax estimate for Pierce/King area (~10.1%) on materials only.
  // Delivery freight is generally non-taxable in WA when separately stated.
  const TAX_RATE = 0.101;
  // Saturday delivery surcharge — flat $40 added when delivery date falls on Saturday.
  const SATURDAY_SURCHARGE = 40;
  const isSaturdayDelivery = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() === 6 : false;
  const saturdaySurcharge = isSaturdayDelivery ? SATURDAY_SURCHARGE : 0;
  const discountedSubtotal = Math.max(0, totalPrice - promo.discount);
  const estimatedTax = Math.round(discountedSubtotal * TAX_RATE * 100) / 100;
  const finalTotal = discountedSubtotal + totalDeliveryFee + saturdaySurcharge + estimatedTax;
  // Same-day cutoff: 12 PM (noon) Mon–Sat. Before cutoff on a business day,
  // today is selectable. Otherwise the next business day (skipping Sunday).
  const minDate = (() => {
    const now = new Date();
    const dow = now.getDay(); // 0=Sun, 6=Sat
    const hour = now.getHours();
    const fmt = (d: Date) => {
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const day = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${day}`;
    };
    const target = new Date(now);
    target.setHours(0, 0, 0, 0);
    // Sunday: dispatch closed, earliest delivery is Monday
    if (dow === 0) {
      target.setDate(target.getDate() + 1);
    } else if (hour >= 12) {
      // After cutoff on a business day → next day
      target.setDate(target.getDate() + 1);
      // If next day is Sunday, push to Monday
      if (target.getDay() === 0) target.setDate(target.getDate() + 1);
    }
    return fmt(target);
  })();
  // Refs for auto-advance
  const nameRef = useRef<HTMLInputElement>(null);
  const phoneRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  const dateBtnRef = useRef<HTMLButtonElement>(null);
  const timeBtnRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const t = useT();
  // Debounced address calculation
  useEffect(() => {
    if (!addressInput.trim() || view !== 'cart') {
      if (!addressInput.trim()) {
        clearDeliveryInfo();
        setAddressError('');
      }
      return;
    }
    const timer = setTimeout(async () => {
      setIsCalculatingDelivery(true);
      setAddressError('');
      try {
        const query = encodeURIComponent(addressInput + ', WA');
        const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
        const data = await res.json();
        if (!data || data.length === 0) {
          setAddressError('Address not found. Please try a more specific address.');
          clearDeliveryInfo();
        } else {
          const lat = parseFloat(data[0].lat);
          const lon = parseFloat(data[0].lon);
          const zoneInfo = getZoneInfo(lat, lon);
          setDeliveryInfo(addressInput, zoneInfo.fee, zoneInfo.zone);
          // Auto-advance to name if address is valid
          if (!name) {
            setTimeout(() => nameRef.current?.focus(), 100);
          }
        }
      } catch (err) {
        setAddressError('Could not calculate delivery. Please try again.');
        clearDeliveryInfo();
      } finally {
        setIsCalculatingDelivery(false);
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [addressInput, view, setDeliveryInfo, clearDeliveryInfo, setIsCalculatingDelivery]);
  const formatPhone = (val: string) => {
    const cleaned = val.replace(/\D/g, '');
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (!match) return val;
    if (!match[2]) return match[1];
    if (!match[3]) return `(${match[1]}) ${match[2]}`;
    return `(${match[1]}) ${match[2]}-${match[3]}`;
  };
  const isAddressValid = addressInput && !addressError && !isCalculatingDelivery;
  const isNameValid = name.trim().length > 1;
  const isPhoneValid = phone.replace(/\D/g, '').length >= 10;
  const isEmailValid = email.includes('@') && email.includes('.');
  const isDateValid = !!deliveryDate;
  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        alert('File size must be less than 5MB');
        return;
      }
      setSitePhoto(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setSitePhotoPreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };
  const removePhoto = () => {
    setSitePhoto(null);
    setSitePhotoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  const handleContinueShopping = () => {
    closeCart();
    setView('cart');
    const el = document.getElementById('materials');
    if (el) {
      setTimeout(() => el.scrollIntoView({
        behavior: 'smooth'
      }), 300);
    }
  };
  const handleCheckout = () => {
    // Validate required fields before proceeding
    if (!addressInput.trim() || !name.trim() || !phone.trim() || !email.trim() || !deliveryDate || !deliveryTime) {
      // We could set an error state here, but for now we'll just rely on HTML5 form validation
      // by triggering a form submit on a hidden button, or just alert.
      // Since we are moving this to a form, we should let the form handle the submit.
      return;
    }
    setView('checkout');
  };
  const handleCartSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !deliveryTime) {
      return;
    }
    setView('checkout');
  };
  const handleBackToCart = () => {
    setView('cart');
  };
  const handleSubmitOrder = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsProcessingPayment(true);
    try {
      const orderItems = items.map(item => {
        const isBark = isLightMaterial(item.product);
        const minChargeYards = 3;
        const smallLoadThreshold = isBark ? 12 : 10;
        // Partial last-load items in a split don't get the 3 yd min bump.
        const isSplitDelivery = /\(Delivery \d+\)/.test(item.product);
        const billableYards = isSplitDelivery ? item.quantity : Math.max(item.quantity, minChargeYards);
        const surcharge = (() => {
          if (billableYards >= smallLoadThreshold) return 1;
          if (billableYards >= 5) return 1 + (smallLoadThreshold - billableYards) * 0.1;
          const at5 = 1 + (smallLoadThreshold - 5) * 0.1;
          return at5 + (5 - billableYards) * 0.15;
        })();
        const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
        let total = billableYards * effectivePrice;
        const fullLoad = isBark ? 15 : 12;
        const bulkPct = item.quantity >= fullLoad * 4 ? 0.1 : item.quantity >= (isBark ? fullLoad : fullLoad * 2) ? 0.05 : 0;
        const hasBulk = bulkPct > 0;
        if (hasBulk) {
          total = Math.round(total * (1 - bulkPct));
        }
        const minNote = !isSplitDelivery && item.quantity < minChargeYards ? ` (${minChargeYards} yd min charge)` : '';
        const bulkNote = hasBulk ? ` (${Math.round(bulkPct * 100)}% bulk discount)` : '';
        // When the last load was rounded UP to the 5yd minimum, show the
        // actual material yards needed alongside what's billed so dispatch
        // and the customer both see the same breakdown.
        const bumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
        const yardsLabel = bumped ? `${item.requestedQuantity} yds material · billed as ${item.quantity} yds (3 yd order min)` : `${item.quantity} yds`;
        return `${item.product} (${item.category}) — ${yardsLabel}${minNote}${bulkNote} = ${total}`;
      }).join('\n');
      const deliveryDow = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() : -1;
      const isSatDelivery = deliveryDow === 6;
      const timeLabel = deliveryTime === 'morning' ? 'Morning (7-11 AM)' : deliveryTime === 'afternoon' ? isSatDelivery ? 'Afternoon (12-1 PM)' : 'Afternoon (12-4 PM)' : isSatDelivery ? 'Flexible (before 1 PM)' : 'Flexible';
      const deliveryFeeLabel = totalDeliveryFee > 0 ? `${totalDeliveryFee.toFixed(2)}` : deliveryZone === null ? 'Custom Quote' : 'FREE';
      const promoLine = promo.code && promo.discount > 0 ? `\nPromo Code: ${promo.code} (10% off materials, −${promo.discount.toFixed(2)})` : '';
      const saturdayLine = saturdaySurcharge > 0 ? `\nSaturday Delivery Surcharge: ${saturdaySurcharge.toFixed(2)}` : '';
      const subMethodLabel = payOnDeliverySubMethod.charAt(0).toUpperCase() + payOnDeliverySubMethod.slice(1);
      const paymentLabel = paymentMethod === 'card' ? 'Pay Online' : `Pay on Delivery (${subMethodLabel})`;
      // Order-source analytics — tag the email so you can see whether the
      // customer built this order in the Instant Estimator, the Shopping Cart
      // (Product page / Materials hub), or a mix of both.
      const sourceTags = new Set(items.map(it => (it as any).source || 'cart'));
      const orderSourceLabel = sourceTags.size > 1 ? 'Mixed (Instant Estimator + Shopping Cart)' : sourceTags.has('estimator') ? 'Instant Estimator' : 'Shopping Cart';
      const yardsLine = yardsBilledExtra > 0 ? `Material Yards Needed: ${materialYards}\nTotal Billed Yards: ${totalYards} (3 yd order minimum applied)` : `Total Yards: ${totalYards}`;
      const orderSummary = `Order Summary:\n${orderItems}\n\n${yardsLine}\nMaterials Total: ${totalPrice.toFixed(2)}${promoLine}\nDelivery Fee: ${deliveryFeeLabel}${saturdayLine}\nEst. Sales Tax (10.1%): ${estimatedTax.toFixed(2)}\nOrder Total: ${finalTotal.toFixed(2)}\n\nOrder Source: ${orderSourceLabel}\n\nDelivery Address: ${addressInput}\nRequested Date: ${deliveryDate}\nTime Window: ${timeLabel}\nDump Location: ${dumpLocation || 'Not specified'}\nSpecial Instructions: ${instructions || 'None'}\nPayment: ${paymentLabel}`;
      const phoneFormatted = (() => {
        const d = (phone || '').replace(/\D/g, '');
        const ten = d.length === 11 && d.startsWith('1') ? d.slice(1) : d;
        return ten.length === 10 ? `(${ten.slice(0, 3)}) ${ten.slice(3, 6)}-${ten.slice(6)}` : phone;
      })();
      // Build the order record up-front so we can include a live tracking
      // link inside the confirmation email body itself.
      const orderNumber = `GR-${Date.now().toString(36).slice(-5).toUpperCase()}${Math.floor(Math.random() * 90 + 10)}`;
      const trackedOrderPayload = {
        orderId: orderNumber,
        createdAt: Date.now(),
        address: addressInput,
        name,
        phone,
        email,
        date: deliveryDate,
        time: timeLabel,
        instructions: [dumpLocation ? `Dump at: ${dumpLocation}` : '', instructions].filter(Boolean).join(' — '),
        items: items.map(it => ({
          product: it.product,
          quantity: it.quantity,
          pricePerYard: it.pricePerYard
        })),
        totalDeliveryFee: deliveryFee + (saturdaySurcharge || 0),
        estimatedTax,
        finalTotal
      };
      const trackLink = buildShareLink(trackedOrderPayload);
      const trackOrderSection = `TRACK YOUR ORDER\n\nOrder #: ${orderNumber}\n\nFollow your delivery in real time — confirmed, scheduled, dispatched, en route, delivered:\n${trackLink}\n\nBookmark this link or save this email. It works on any device, no login required.`;
      const whatsNext = `WHAT HAPPENS NEXT\n\n1. SMS Confirmation\n   A text confirmation will arrive at ${phoneFormatted} within a few minutes.\n\n2. Dispatch Review\n   Our team verifies your address, materials, and delivery window — usually within 1 business hour.\n\n3. Driver Dispatched\n   On delivery day, we'll text you a live tracking link the moment your driver is en route.\n\n4. Track Anytime\n   Check the status of your order at any time using your tracking link below.`;
      const howToPrepare = `HOW TO PREPARE FOR DELIVERY\n\n• Clear the dump zone — Mark a flat, accessible spot at least 10' wide x 20' long.\n• Driveway clearance — We need 12' of overhead clearance (watch for branches and wires).\n• Move vehicles & obstacles — Cars, trash bins, hoses, and toys should be clear of the path.\n• Surface protection — If dumping on a finished driveway, lay down plywood or a tarp to protect the surface.\n• Be reachable — Keep your phone nearby on delivery day in case the driver needs directions.\n• Have payment ready — If paying on delivery, please have cash, card, or check available.\n\nFull guide: https://253gravel.com/#/delivery-prep`;
      const fullEmailBody = `Hi ${name}! Thank you for your order request with 253 Gravel. We've received your details and will contact you shortly to verify your order and confirm delivery.\n\n${orderSummary}\n\n----------\n\n${trackOrderSection}\n\n----------\n\n${whatsNext}\n\n----------\n\n${howToPrepare}\n\nIf you have any questions, call us at (206) 619-3742.\n\nWe look forward to serving you!\n- The 253 Gravel Team`;
      await sendFormEmail({
        subject: '253Gravel — New Order Submission',
        to_name: name,
        from_name: '253 Gravel',
        'Order Items': orderItems,
        'Total Yards': String(totalYards),
        'Materials Total': `${totalPrice.toFixed(2)}`,
        'Promo Code': promo.code && promo.discount > 0 ? `${promo.code} (−${promo.discount.toFixed(2)})` : 'None',
        'Delivery Fee': deliveryFeeLabel,
        'Delivery Zone': deliveryZone !== null ? `Zone ${deliveryZone}` : 'Outside standard zones',
        'Order Total': `${finalTotal.toFixed(2)}`,
        'Order Source': orderSourceLabel,
        Name: name,
        'Delivery Address': addressInput,
        Phone: phone,
        email: email,
        _replyto: email,
        _autoresponse: fullEmailBody,
        message: fullEmailBody,
        'Order Number': orderNumber,
        'Track Order Link': trackLink,
        'Track Order Section': trackOrderSection,
        'What Happens Next': whatsNext,
        'How To Prepare': howToPrepare,
        'Delivery Date': deliveryDate,
        'Delivery Time': timeLabel,
        'Dump Location': dumpLocation || 'Not specified',
        'Special Instructions': instructions || 'None',
        'Payment Method': paymentLabel
      });
      setOrderSummaryData({
        items: [...items],
        totalYards,
        materialYards,
        yardsBilledExtra,
        totalPrice,
        deliveryFeeLabel,
        saturdaySurcharge,
        estimatedTax,
        finalTotal,
        addressInput,
        deliveryDate,
        timeLabel,
        name,
        phone,
        email,
        orderNumber
      });
      // Save order to localStorage for quick reorder
      localStorage.setItem('253gravel_last_order', JSON.stringify({
        items: [...items],
        addressInput,
        name,
        phone,
        email
      }));
      // Save a complete tracked-order record so the customer can track it.
      try {
        saveOrderForTracking(trackedOrderPayload);
      } catch (e) {
        console.error('Could not save tracked order', e);
      }
    } catch (err) {
      console.error('Order submission error:', err);
    } finally {
      setIsProcessingPayment(false);
    }
    setView('success');
  };
  const handleCloseSuccess = () => {
    setView('cart');
    setAddressInput('');
    setName('');
    setPhone('');
    setEmail('');
    setDeliveryDate('');
    setDeliveryTime('');
    setInstructions('');
    setDumpLocation('');
    clearCart();
    closeCart();
  };
  const inputClass = 'w-full px-4 py-3.5 bg-white/60 dark:bg-white/10 backdrop-blur-sm border border-white/30 dark:border-white/10 rounded-xl text-stone-900 dark:text-white placeholder-stone-500 dark:placeholder-stone-400 focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange/50 focus:shadow-[0_0_15px_rgba(255,140,50,0.15)] transition-all text-base font-medium';
  // Helper to build delivery zones URL with address
  const deliveryZonesUrl = (addr?: string) => {
    const params = addr?.trim() ? `?address=${encodeURIComponent(addr)}` : '';
    return `/#/delivery-zones${params}`;
  };
  // Format card number with spaces
  const formatCardNumber = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };
  // Format expiry as MM/YY
  const formatExpiry = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, 4);
    if (digits.length >= 3) {
      return digits.slice(0, 2) + '/' + digits.slice(2);
    }
    return digits;
  };
  const handleReorder = () => {
    const lastOrderStr = localStorage.getItem('253gravel_last_order');
    if (lastOrderStr) {
      const lastOrder = JSON.parse(lastOrderStr);
      if (lastOrder.items && lastOrder.items.length > 0) {
        loadSavedCart(lastOrder.items);
        setAddressInput(lastOrder.addressInput || '');
        setName(lastOrder.name || '');
        setPhone(lastOrder.phone || '');
        setEmail(lastOrder.email || '');
      }
    }
  };
  const getDeliveryDayText = () => {
    if (!deliveryDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const delDate = new Date(deliveryDate + 'T00:00:00');
    const diffTime = delDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'Same-day delivery!';
    if (diffDays === 1) return 'Delivering tomorrow!';
    return `Delivering in ${diffDays} days`;
  };
  return <AnimatePresence data-id="element-187">
      {isCartOpen && <>
          {/* Backdrop */}
          <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={() => {
        closeCart();
        setView('cart');
      }} className="fixed inset-0 bg-stone-900/40 dark:bg-black/70 backdrop-blur-md z-[60]" data-id="element-188" />

          {/* Modal Container */}
          <div className="fixed inset-0 flex items-center justify-center p-4 z-[70] pointer-events-none" role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title" data-id="element-189">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 300
        }} className="w-full max-w-5xl bg-white dark:bg-[#2a2a2a]/90 backdrop-blur-xl shadow-[0_8px_60px_rgba(0,0,0,0.15)] dark:shadow-[0_8px_60px_rgba(0,0,0,0.5)] flex flex-col rounded-2xl border border-stone-200 dark:border-white/10 max-h-[90dvh] overflow-hidden pointer-events-auto" role="dialog" aria-modal="true" aria-labelledby="cart-drawer-title" data-id="element-190">
              {/* Header */}
              <div className="flex items-center justify-between px-8 py-6 border-b border-stone-200 dark:border-white/10 shrink-0 bg-gradient-to-b from-stone-50 to-transparent dark:from-white/5 dark:to-transparent" data-id="element-191">
                <div className="flex items-center space-x-4" data-id="element-192">
                  {view === 'checkout' && <button onClick={handleBackToCart} className="p-2 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-lg hover:bg-black/5 dark:hover:bg-white/10 mr-1" aria-label="Back to cart" data-id="element-193">
                      <ArrowLeft className="w-6 h-6" data-id="element-194" />
                    </button>}
                  <Truck className="w-7 h-7 text-brand-charcoal dark:text-white" data-id="element-195" />
                  <h2 id="cart-drawer-title" className="font-heading text-xl font-bold text-brand-charcoal dark:text-white" data-id="element-196">
                    {view === 'cart' ? t('cart.title') : view === 'checkout' ? t('cart.checkout') : t('cart.orderConfirmed')}
                  </h2>
                </div>
                <button onClick={() => {
              closeCart();
              setView('cart');
            }} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white transition-colors rounded-full hover:bg-black/5 dark:hover:bg-white/10" aria-label="Close cart" data-id="element-197">
                  <X className="w-6 h-6" data-id="element-198" />
                </button>
              </div>
              {/* ===== CART VIEW ===== */}
              {view === 'cart' && <form id="cart-form" onSubmit={handleCartSubmit} className="flex-1 overflow-y-auto" data-id="element-199">
                  {/* Sticky Progress Stepper — only when there are items */}
                  {items.length > 0 && <div className="sticky top-0 z-[55] bg-white/85 dark:bg-[#2a2a2a]/85 backdrop-blur-md border-b border-stone-200/60 dark:border-white/5 px-6 py-2.5" data-id="element-200">
                      <div className="flex items-center justify-between max-w-full" data-id="element-201">
                        {[{
                  id: 'address',
                  label: 'Address',
                  isComplete: isAddressValid,
                  isActive: !isAddressValid
                }, {
                  id: 'name',
                  label: 'Name',
                  isComplete: isNameValid,
                  isActive: isAddressValid && !isNameValid
                }, {
                  id: 'phone',
                  label: 'Phone',
                  isComplete: isPhoneValid,
                  isActive: isAddressValid && isNameValid && !isPhoneValid
                }, {
                  id: 'email',
                  label: 'Email',
                  isComplete: isEmailValid,
                  isActive: isAddressValid && isNameValid && isPhoneValid && !isEmailValid
                }, {
                  id: 'date',
                  label: 'Date',
                  isComplete: isDateValid,
                  isActive: isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid
                }, {
                  id: 'time',
                  label: 'Time',
                  isComplete: !!deliveryTime,
                  isActive: isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime
                }].map((step, idx, arr) => <Fragment key={step.id} data-id="element-202">
                            <div className="flex items-center gap-1.5 relative z-10 shrink-0" data-id="element-203">
                              <div className={`w-4 h-4 rounded-full flex items-center justify-center text-[9px] font-bold transition-all duration-300 ${step.isComplete ? 'bg-green-700 text-white' : step.isActive ? 'bg-brand-orange/90 text-white ring-2 ring-brand-orange/15' : 'bg-stone-200 dark:bg-white/10 text-stone-400 dark:text-stone-500'}`} data-id="element-204">
                                {step.isComplete ? <Check className="w-2.5 h-2.5" strokeWidth={3} data-id="element-205" /> : idx + 1}
                              </div>
                              <span className={`text-[10px] font-semibold tracking-wide hidden sm:block ${step.isComplete ? 'text-green-700 dark:text-green-500' : step.isActive ? 'text-brand-orange/90' : 'text-stone-400 dark:text-stone-500'}`} data-id="element-206">
                                {step.label}
                              </span>
                            </div>
                            {idx < arr.length - 1 && <div className="flex-1 h-px mx-1.5 bg-stone-200 dark:bg-white/10 relative" data-id="element-207">
                                <div className="absolute top-0 left-0 h-full bg-green-700 transition-all duration-500" style={{
                      width: step.isComplete ? '100%' : '0%'
                    }} data-id="element-208" />
                              </div>}
                          </Fragment>)}
                      </div>
                    </div>}
                  {/* Items */}
                  <div className="p-8" data-id="element-209">
                    {items.length === 0 ? <div className="h-full flex flex-col items-center justify-center text-center space-y-8 py-16" data-id="element-210">
                        <div className="w-28 h-28 bg-stone-100 dark:bg-white/5 rounded-full flex items-center justify-center border border-stone-200 dark:border-white/10 shadow-inner" data-id="element-211">
                          <Truck className="w-12 h-12 text-brand-muted opacity-50" data-id="element-212" />
                        </div>
                        <div data-id="element-213">
                          <h3 className="font-heading text-2xl sm:text-3xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-214">
                            Your truck is empty
                          </h3>
                          <p className="text-brand-muted dark:text-stone-300 text-lg max-w-[300px] mx-auto" data-id="element-215">
                            Looks like you haven't added any materials to your
                            truck yet.
                          </p>
                        </div>
                        <div className="flex flex-col gap-3 w-full max-w-[280px]" data-id="element-216">
                          <button onClick={handleContinueShopping} className="bg-brand-charcoal dark:bg-white/10 hover:bg-brand-charcoal/90 dark:hover:bg-white/20 text-white px-10 py-4 rounded-xl font-bold text-lg transition-colors border border-transparent dark:border-white/10 backdrop-blur-sm w-full" data-id="element-217">
                            Browse Materials
                          </button>
                          {localStorage.getItem('253gravel_last_order') && <button type="button" onClick={handleReorder} className="bg-brand-orange/10 hover:bg-brand-orange/20 text-brand-orange border border-brand-orange/30 px-10 py-4 rounded-xl font-bold text-lg transition-colors w-full" data-id="element-218">
                              Reorder Last Order
                            </button>}
                        </div>
                      </div> : <div className="space-y-5" data-id="element-219">
                        {items.map(item => <div key={item.id} className="flex flex-col sm:flex-row gap-5 p-5 bg-stone-50 dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-stone-200 dark:border-white/10 shadow-sm dark:shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] overflow-hidden" data-id="element-220">
                            <div className="flex-1" data-id="element-221">
                              <p className="text-sm font-bold text-brand-muted dark:text-stone-400 mb-1.5 uppercase tracking-wider" data-id="element-222">
                                {item.category}
                              </p>
                              <h4 className="font-heading font-extrabold text-lg text-brand-charcoal dark:text-white mb-1.5" data-id="element-223">
                                {item.product}
                              </h4>
                              {(() => {
                      const isLight = isLightMaterial(item.product);
                      const minChargeYards = 3;
                      const smallLoadThreshold = isLight ? 12 : 10;
                      const isSplitDelivery = /\(Delivery \d+\)/.test(item.product);
                      const billableYards = isSplitDelivery ? item.quantity : Math.max(item.quantity, minChargeYards);
                      const surcharge = (() => {
                        if (billableYards >= smallLoadThreshold) return 1;
                        if (billableYards >= 5) return 1 + (smallLoadThreshold - billableYards) * 0.1;
                        const at5 = 1 + (smallLoadThreshold - 5) * 0.1;
                        return at5 + (5 - billableYards) * 0.15;
                      })();
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      const fullLoadDisc = isLight ? 15 : 12;
                      const bulkPct = item.quantity >= fullLoadDisc * 4 ? 0.1 : item.quantity >= (isLight ? fullLoadDisc : fullLoadDisc * 2) ? 0.05 : 0;
                      const hasBulk = bulkPct > 0;
                      const displayPrice = hasBulk ? Math.ceil(effectivePrice * (1 - bulkPct)) : effectivePrice;
                      return <p className="text-base text-brand-muted dark:text-stone-200 font-medium" data-id="element-224">
                                    ${displayPrice} / yard
                                    {hasBulk && <span className="text-sm text-green-600 dark:text-green-400 ml-1.5 font-semibold" data-id="element-225">
                                        ({Math.round(bulkPct * 100)}% bulk
                                        discount)
                                      </span>}
                                    {!hasBulk && !isSplitDelivery && item.quantity < minChargeYards && <span className="text-sm text-brand-orange ml-1.5" data-id="element-226">
                                          ({minChargeYards} yd min charge)
                                        </span>}
                                    {!hasBulk && billableYards < smallLoadThreshold && (isSplitDelivery || item.quantity >= minChargeYards) && <span className="text-sm text-brand-orange ml-1.5" data-id="element-227">
                                          (small load · ${item.pricePerYard}/yd
                                          at {smallLoadThreshold}+)
                                        </span>}
                                  </p>;
                    })()}
                            </div>
                            <div className="flex flex-col gap-2 min-w-0" data-id="element-228">
                              {/* Price row */}
                              {(() => {
                      const isLight = isLightMaterial(item.product);
                      const minChargeYards = 3;
                      const smallLoadThreshold = isLight ? 12 : 10;
                      const isSplitDelivery = /\(Delivery \d+\)/.test(item.product);
                      const billableYards = isSplitDelivery ? item.quantity : Math.max(item.quantity, minChargeYards);
                      const surcharge = (() => {
                        if (billableYards >= smallLoadThreshold) return 1;
                        if (billableYards >= 5) return 1 + (smallLoadThreshold - billableYards) * 0.1;
                        const at5 = 1 + (smallLoadThreshold - 5) * 0.1;
                        return at5 + (5 - billableYards) * 0.15;
                      })();
                      const effectivePrice = Math.ceil(item.pricePerYard * surcharge);
                      let itemTotal = billableYards * effectivePrice;
                      const baseProduct = (n: string) => n.replace(/\s*\(Delivery \d+\)/, '');
                      const aggYards = items.filter(i => baseProduct(i.product) === baseProduct(item.product)).reduce((s, i) => s + i.quantity, 0);
                      const fullLoad = isLight ? 15 : 12;
                      const bulkPct2 = aggYards >= fullLoad * 4 ? 0.1 : aggYards >= (isLight ? fullLoad : fullLoad * 2) ? 0.05 : 0;
                      const hasBulk = bulkPct2 > 0;
                      if (hasBulk) {
                        itemTotal = Math.round(itemTotal * (1 - bulkPct2));
                      }
                      return <div className="flex items-baseline gap-2 flex-wrap" data-id="element-229">
                                    <p className="font-heading font-extrabold text-xl text-brand-charcoal dark:text-white" data-id="element-230">
                                      ${itemTotal}
                                    </p>
                                    {hasBulk && <span className="text-xs text-green-600 dark:text-green-400 font-semibold" data-id="element-231">
                                        {Math.round(bulkPct2 * 100)}% bulk
                                        discount
                                      </span>}
                                  </div>;
                    })()}

                              {/* Quantity + Delete row */}
                              <div className="flex items-center gap-3" data-id="element-232">
                                {(() => {
                        const isBarkMulch = isLightMaterial(item.product);
                        const fullLoad = isBarkMulch ? 15 : 12;
                        const isFullLoadMultiple = item.quantity % fullLoad === 0;
                        const loadCount = item.quantity / fullLoad;
                        const handleMinus = () => {
                          if (isFullLoadMultiple && loadCount > 1) {
                            updateQuantity(item.id, item.quantity - fullLoad);
                          } else {
                            updateQuantity(item.id, item.quantity - 1);
                          }
                        };
                        const maxQty = isBarkMulch ? 60 : 48;
                        const handlePlus = () => {
                          if (item.quantity >= maxQty) return;
                          if (isFullLoadMultiple) {
                            updateQuantity(item.id, Math.min(item.quantity + fullLoad, maxQty));
                          } else {
                            updateQuantity(item.id, Math.min(item.quantity + 1, maxQty));
                          }
                        };
                        const tonsEstimate = (item.quantity * getDensity(item.product)).toFixed(1);
                        return <div className="flex flex-col items-center gap-1" data-id="element-233">
                                      <div className="flex items-center bg-white/60 dark:bg-white/10 backdrop-blur-sm border border-white/30 dark:border-white/10 rounded-lg overflow-hidden min-w-[120px] sm:min-w-[140px]" data-id="element-234">
                                        <button type="button" onClick={handleMinus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" aria-label="Decrease quantity" data-id="element-235">
                                          <Minus className="w-5 h-5" data-id="element-236" />
                                        </button>
                                        <div className="flex items-center justify-center py-1.5 flex-1 whitespace-nowrap gap-1.5" data-id="element-237">
                                          <span className="font-bold text-base text-brand-charcoal dark:text-white leading-none" data-id="element-238">
                                            {isFullLoadMultiple ? loadCount : item.quantity}
                                          </span>
                                          <span className="text-[10px] text-brand-muted dark:text-stone-300 uppercase tracking-wider font-bold" data-id="element-239">
                                            {isFullLoadMultiple ? loadCount === 1 ? 'Load' : 'Loads' : 'yd'}
                                          </span>
                                        </div>
                                        <button type="button" onClick={handlePlus} className="p-2.5 text-brand-muted hover:text-brand-charcoal dark:hover:text-white hover:bg-black/5 dark:hover:bg-white/10 transition-colors" aria-label="Increase quantity" data-id="element-240">
                                          <Plus className="w-5 h-5" data-id="element-241" />
                                        </button>
                                      </div>
                                      <span className="text-xs text-stone-400 dark:text-stone-400 font-medium" data-id="element-242">
                                        {isFullLoadMultiple ? `${item.quantity} yds` : null}
                                        {isFullLoadMultiple ? ' · ' : ''}≈{' '}
                                        {tonsEstimate} tons
                                      </span>
                                    </div>;
                      })()}

                                {/* Delete button */}
                                <button onClick={() => removeFromCart(item.id)} className="p-2 text-brand-muted hover:text-brand-orange transition-colors shrink-0" title="Remove item" aria-label={`Remove ${item.product} from cart`} data-id="element-243">
                                  <Trash2 className="w-5 h-5" data-id="element-244" />
                                </button>
                              </div>
                            </div>
                          </div>)}
                      </div>}
                  </div>

                  {/* Contractor Prompt */}
                  {(() => {
              const productYards: Record<string, number> = {};
              const baseName = (n: string) => n.replace(/\s*\(Delivery \d+\)/, '');
              for (const item of items) {
                const base = baseName(item.product);
                productYards[base] = (productYards[base] || 0) + item.quantity;
              }
              return Object.values(productYards).some(y => y > 24);
            })() && <div className="mx-8 mb-2 p-4 bg-brand-orange/10 dark:bg-brand-orange/10 border border-brand-orange/30 rounded-xl flex items-start gap-3" data-id="element-245">
                      <Info className="w-5 h-5 text-brand-orange shrink-0 mt-0.5" data-id="element-246" />
                      <div data-id="element-247">
                        <p className="text-sm font-bold text-brand-charcoal dark:text-white" data-id="element-248">
                          Are you a contractor?
                        </p>
                        <p className="text-xs text-brand-muted dark:text-stone-400 mt-0.5" data-id="element-249">
                          Get priority scheduling, volume pricing, and dedicated
                          support.{' '}
                          <a href="/#/subcontractors" className="text-brand-orange font-bold underline hover:text-brand-orange/80 transition-colors" onClick={() => closeCart()} data-id="element-250">
                            Sign up on our Contractors page →
                          </a>
                        </p>
                      </div>
                    </div>}

                  {/* Delivery Details */}
                  {items.length > 0 && <div className={`px-8 pb-8 pt-8 ${showCalendar ? 'relative z-[200]' : ''}`} data-id="element-251">
                      <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-stone-500 tracking-tight mb-5" data-id="element-252">
                        Delivery Details
                      </h3>
                      <div className="space-y-4 bg-stone-50 dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-stone-200 dark:border-white/10 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)] p-5" data-id="element-253">
                        {/* Address */}
                        <div data-id="element-254">
                          <label className="flex items-center justify-between text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-255">
                            <span className="flex items-center" data-id="element-256">
                              <MapPin className="w-5 h-5 mr-2 text-brand-muted" data-id="element-257" />
                              Delivery Address
                            </span>
                          </label>
                          <input type="text" value={addressInput} onChange={e => setAddressInput(e.target.value)} placeholder="123 Main St, Tacoma, WA" autoComplete="street-address" className={inputClass} required aria-required="true" aria-invalid={!!addressError} data-id="element-258" />
                          <div aria-live="polite" className="mt-1.5" data-id="element-259">
                            {isCalculatingDelivery && <Skeleton className="h-5 w-[60%] rounded" data-id="element-260" />}
                            {addressError && !isCalculatingDelivery && <p className="text-sm text-red-500 font-medium" data-id="element-261">
                                {addressError}
                              </p>}
                            {!isCalculatingDelivery && !addressError && addressInput && deliveryZone !== null && <p className="text-sm text-green-600 dark:text-green-400 flex items-center font-medium" data-id="element-262">
                                  <CheckCircle2 className="w-4 h-4 mr-1" data-id="element-263" />
                                  Zone {deliveryZone} (
                                  {deliveryFee === 0 ? 'Free Delivery' : deliveryCount > 1 ? `+$${totalDeliveryFee.toFixed(0)} for ${deliveryCount} deliveries` : `+$${deliveryFee} Delivery`}
                                  ) —{' '}
                                  <a href={deliveryZonesUrl(addressInput)} target="_blank" rel="noopener noreferrer" className="underline hover:text-green-300 transition-colors" data-id="element-264">
                                    View Zones
                                  </a>
                                </p>}
                            {!isCalculatingDelivery && !addressError && addressInput && deliveryZone === null && <p className="text-sm text-brand-orange flex items-center font-medium" data-id="element-265">
                                  <Info className="w-4 h-4 mr-1" data-id="element-266" />
                                  Outside standard zones. We'll call with a
                                  custom quote.
                                </p>}
                          </div>
                        </div>

                        {/* Name */}
                        <div data-id="element-267">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-268">
                            <FileText className="w-5 h-5 mr-2 text-brand-muted" data-id="element-269" />
                            Full Name
                          </label>
                          <input ref={nameRef} type="text" value={name} onChange={e => setName(e.target.value)} onKeyDown={e => {
                    if (e.key === 'Enter' && isNameValid) {
                      e.preventDefault();
                      setTimeout(() => phoneRef.current?.focus(), 100);
                    }
                  }} placeholder="John Doe" autoComplete="name" className={`${inputClass} ${isAddressValid && !isNameValid ? 'ring-2 ring-[#1DB954] border-[#1DB954]' : ''}`} required aria-required="true" data-id="element-270" />
                        </div>

                        {/* Phone & Email */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-id="element-271">
                          <div data-id="element-272">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-273">
                              <Phone className="w-5 h-5 mr-2 text-brand-muted" data-id="element-274" />
                              Phone Number
                            </label>
                            <input ref={phoneRef} type="tel" value={phone} onChange={e => {
                      const formatted = formatPhone(e.target.value);
                      setPhone(formatted);
                      if (formatted.length === 14) {
                        setTimeout(() => emailRef.current?.focus(), 100);
                      }
                    }} placeholder="(206) 555-1234" autoComplete="tel" className={`${inputClass} ${isAddressValid && isNameValid && !isPhoneValid ? 'ring-2 ring-[#1DB954] border-[#1DB954]' : ''}`} required aria-required="true" aria-invalid={!isPhoneValid && phone.length > 0} data-id="element-275" />
                          </div>
                          <div data-id="element-276">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-277">
                              <Mail className="w-5 h-5 mr-2 text-brand-muted" data-id="element-278" />
                              Email Address
                            </label>
                            <input ref={emailRef} type="email" value={email} onChange={e => setEmail(e.target.value)} onKeyDown={e => {
                      if (e.key === 'Enter' && isEmailValid) {
                        e.preventDefault();
                        setShowCalendar(true);
                        setTimeout(() => dateBtnRef.current?.focus(), 100);
                      }
                    }} placeholder="you@example.com" autoComplete="email" className={`${inputClass} ${isAddressValid && isNameValid && isPhoneValid && !isEmailValid ? 'ring-2 ring-[#1DB954] border-[#1DB954]' : ''}`} required aria-required="true" aria-invalid={!isEmailValid && email.length > 0} data-id="element-279" />
                          </div>
                        </div>

                        {/* Date & Time Row */}
                        <div className="grid grid-cols-2 gap-4" data-id="element-280">
                          <div className={`relative ${showCalendar ? 'z-[100]' : ''}`} data-id="element-281">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-282">
                              <CalendarDays className="w-5 h-5 mr-2 text-brand-muted" data-id="element-283" />
                              Date
                            </label>
                            <button ref={dateBtnRef} type="button" onClick={() => setShowCalendar(!showCalendar)} className={`${inputClass} text-left flex items-center justify-between w-full ${isAddressValid && isNameValid && isPhoneValid && isEmailValid && !isDateValid ? 'ring-2 ring-[#1DB954] border-[#1DB954]' : ''}`} data-id="element-284">
                              <span className={deliveryDate ? 'text-stone-900 dark:text-white' : 'text-stone-400'} data-id="element-285">
                                {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          month: 'short',
                          day: 'numeric'
                        }) : 'Select'}
                              </span>
                              <CalendarDays className="w-5 h-5 text-stone-300" data-id="element-286" />
                            </button>
                            {/* Calendar Dropdown */}
                            <AnimatePresence data-id="element-287">
                              {showCalendar && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 left-0 w-[320px] bg-white/95 dark:bg-[#2a2a2a]/95 backdrop-blur-xl border border-white/30 dark:border-white/10 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-5" data-id="element-288">
                                  {(() => {
                          const today = new Date();
                          const calMonth = calendarMonth || new Date(today.getFullYear(), today.getMonth(), 1);
                          const year = calMonth.getFullYear();
                          const month = calMonth.getMonth();
                          const firstDay = new Date(year, month, 1).getDay();
                          const daysInMonth = new Date(year, month + 1, 0, 0).getDate();
                          const monthName = calMonth.toLocaleDateString('en-US', {
                            month: 'long',
                            year: 'numeric'
                          });
                          const days = [];
                          for (let i = 0; i < firstDay; i++) days.push(null);
                          for (let d = 1; d <= daysInMonth; d++) days.push(d);
                          return <>
                                        <div className="flex items-center justify-between mb-4" data-id="element-289">
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month - 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-290">
                                            <ChevronLeft className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-291" />
                                          </button>
                                          <span className="text-base font-extrabold text-stone-900 dark:text-white tracking-tight" data-id="element-292">
                                            {monthName}
                                          </span>
                                          <button type="button" onClick={() => setCalendarMonth(new Date(year, month + 1, 1))} className="p-2 hover:bg-black/5 dark:hover:bg-white/10 rounded-lg transition-colors" data-id="element-293">
                                            <ChevronRight className="w-5 h-5 text-stone-600 dark:text-stone-300" data-id="element-294" />
                                          </button>
                                        </div>
                                        <div className="grid grid-cols-7 gap-1 text-center mb-2" data-id="element-295">
                                          {['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'].map(d => <span key={d} className="text-xs font-bold text-stone-500 dark:text-stone-400 py-1 uppercase" data-id="element-296">
                                              {d}
                                            </span>)}
                                        </div>
                                        <div className="grid grid-cols-7 gap-1" data-id="element-297">
                                          {days.map((day, i) => {
                                if (day === null) return <span key={`empty-${i}`} data-id="element-298" />;
                                const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                                const isPast = dateStr < minDate;
                                const isSelected = dateStr === deliveryDate;
                                const isToday = dateStr === today.toISOString().split('T')[0];
                                const cellDow = new Date(dateStr + 'T12:00:00').getDay();
                                const isSat = cellDow === 6;
                                return <button key={dateStr} type="button" disabled={isPast} onClick={() => {
                                  setDeliveryDate(dateStr);
                                  setShowCalendar(false);
                                  setTimeout(() => {
                                    timeBtnRef.current?.focus();
                                    setShowTimeDropdown(true);
                                  }, 100);
                                }} title={isSat && !isPast ? 'Saturday delivery: +$40 weekend surcharge' : undefined} className={`relative text-base font-bold py-2 rounded-xl transition-colors ${isPast ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed' : isSelected ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : isToday ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-299">
                                                {day}
                                                {isSat && !isPast && <span className={`absolute -top-0.5 -right-0.5 text-[8px] font-extrabold leading-none px-1 py-0.5 rounded ${isSelected ? 'bg-white/30 text-white' : 'bg-brand-orange/20 text-brand-orange'}`} data-id="element-300">
                                                    +$40
                                                  </span>}
                                              </button>;
                              })}
                                        </div>
                                      </>;
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                          <div className="relative" data-id="element-301">
                            <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-302">
                              <Clock className="w-5 h-5 mr-2 text-brand-muted" data-id="element-303" />
                              Time
                            </label>
                            <input type="hidden" value={deliveryTime} required data-id="element-304" />
                            <button ref={timeBtnRef} type="button" onClick={() => setShowTimeDropdown(!showTimeDropdown)} className={`${inputClass} text-left flex items-center justify-between w-full ${isAddressValid && isNameValid && isPhoneValid && isEmailValid && isDateValid && !deliveryTime ? 'ring-2 ring-[#1DB954] border-[#1DB954]' : ''}`} data-id="element-305">
                              <span className={deliveryTime ? 'text-stone-900 dark:text-white' : 'text-stone-400'} data-id="element-306">
                                {deliveryTime === 'morning' ? 'Morning (7–11 AM)' : deliveryTime === 'afternoon' ? 'Afternoon (12–4 PM)' : deliveryTime === 'flexible' ? 'Flexible' : 'Select'}
                              </span>
                              <Clock className="w-5 h-5 text-stone-300" data-id="element-307" />
                            </button>
                            <AnimatePresence data-id="element-308">
                              {showTimeDropdown && <motion.div initial={{
                        opacity: 0,
                        y: -4
                      }} animate={{
                        opacity: 1,
                        y: 0
                      }} exit={{
                        opacity: 0,
                        y: -4
                      }} transition={{
                        duration: 0.15
                      }} className="absolute z-50 top-full mt-2 left-0 w-full bg-white/95 dark:bg-[#2a2a2a]/95 backdrop-blur-xl border border-white/30 dark:border-white/10 rounded-2xl shadow-[0_8px_30px_rgba(0,0,0,0.3)] p-3 space-y-1" data-id="element-309">
                                  {(() => {
                          // Gate windows by the selected day's dispatch hours.
                          // Mon–Fri 6a–5p, Sat ends at 1 PM, Sun closed.
                          const dow = deliveryDate ? new Date(deliveryDate + 'T12:00:00').getDay() : -1;
                          const isSat = dow === 6;
                          const isSun = dow === 0;
                          const noDate = !deliveryDate;
                          // Detect same-day: morning windows can't be
                          // guaranteed since trucks are already routed.
                          const todayStr = (() => {
                            const d = new Date();
                            const y = d.getFullYear();
                            const m = String(d.getMonth() + 1).padStart(2, '0');
                            const day = String(d.getDate()).padStart(2, '0');
                            return `${y}-${m}-${day}`;
                          })();
                          const isSameDay = deliveryDate === todayStr;
                          const opts = [{
                            value: 'morning',
                            label: 'Morning (7–11 AM)',
                            disabled: isSun,
                            note: isSameDay ? 'Best effort — not guaranteed' : null
                          }, {
                            value: 'afternoon',
                            // Saturday: dispatch ends at 1 PM, so afternoon is unavailable
                            label: isSat ? 'Afternoon — Sat ends 1 PM' : 'Afternoon (12–4 PM)',
                            disabled: isSun || isSat,
                            note: isSameDay && !isSat ? 'Recommended for same-day' : null
                          }, {
                            value: 'flexible',
                            label: isSat ? 'Flexible (before 1 PM)' : 'Flexible',
                            disabled: isSun,
                            note: isSameDay ? 'Best chance same-day' : null
                          }];
                          if (noDate) {
                            return <p className="text-xs text-stone-500 dark:text-stone-400 px-3 py-3 font-medium" data-id="element-310">
                                          Pick a date first.
                                        </p>;
                          }
                          return opts.map(opt => <button key={opt.value} type="button" disabled={opt.disabled} onClick={() => {
                            if (opt.disabled) return;
                            setDeliveryTime(opt.value);
                            setShowTimeDropdown(false);
                          }} className={`w-full text-left px-4 py-3 rounded-xl text-base font-bold transition-colors flex items-center justify-between ${opt.disabled ? 'text-stone-300 dark:text-stone-600 cursor-not-allowed' : deliveryTime === opt.value ? 'bg-brand-orange text-white shadow-[0_0_15px_rgba(255,140,50,0.3)]' : 'text-stone-700 dark:text-stone-200 hover:bg-black/5 dark:hover:bg-white/10'}`} data-id="element-311">
                                        <div className="flex flex-col items-start" data-id="element-312">
                                          <span data-id="element-313">{opt.label}</span>
                                          {opt.note && !opt.disabled && <span className={`text-[10px] font-semibold uppercase tracking-wider mt-0.5 ${opt.value === 'morning' ? deliveryTime === opt.value ? 'text-white/85' : 'text-amber-600 dark:text-amber-400' : deliveryTime === opt.value ? 'text-white/85' : 'text-green-600 dark:text-green-400'}`} data-id="element-314">
                                              {opt.note}
                                            </span>}
                                        </div>
                                        {opt.disabled && <span className="text-[10px] uppercase tracking-wider font-bold text-stone-400" data-id="element-315">
                                            Closed
                                          </span>}
                                      </button>);
                        })()}
                                </motion.div>}
                            </AnimatePresence>
                          </div>
                        </div>

                        {/* Same-Day Morning Warning — trucks are already
                         routed for the morning, so a same-day morning
                         slot is best-effort rather than guaranteed. */}
                        <AnimatePresence data-id="element-316">
                          {(() => {
                    const today = new Date();
                    const y = today.getFullYear();
                    const m = String(today.getMonth() + 1).padStart(2, '0');
                    const d = String(today.getDate()).padStart(2, '0');
                    const todayStr = `${y}-${m}-${d}`;
                    const isSameDay = deliveryDate === todayStr;
                    const showWarning = isSameDay && deliveryTime === 'morning';
                    if (!showWarning) return null;
                    return <motion.div key="sameday-morning-warning" initial={{
                      opacity: 0,
                      height: 0,
                      marginTop: 0
                    }} animate={{
                      opacity: 1,
                      height: 'auto',
                      marginTop: 12
                    }} exit={{
                      opacity: 0,
                      height: 0,
                      marginTop: 0
                    }} className="overflow-hidden" data-id="element-317">
                                <div className="flex items-start gap-2.5 p-3 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-300" data-id="element-318">
                                  <Info className="w-4 h-4 mt-0.5 shrink-0" data-id="element-319" />
                                  <p className="text-sm font-medium leading-snug" data-id="element-320">
                                    <span className="font-bold" data-id="element-321">
                                      Heads up — same-day morning isn't
                                      guaranteed.
                                    </span>{' '}
                                    Trucks are already routed for the morning.
                                    We'll do our best, but{' '}
                                    <button type="button" onClick={() => setDeliveryTime('flexible')} className="underline font-bold hover:text-amber-800 dark:hover:text-amber-200" data-id="element-322">
                                      switch to Flexible
                                    </button>{' '}
                                    for the best chance, or call{' '}
                                    <a href="tel:206-619-3742" className="underline font-bold hover:text-amber-800 dark:hover:text-amber-200" data-id="element-323">
                                      (206) 619-3742
                                    </a>{' '}
                                    to confirm.
                                  </p>
                                </div>
                              </motion.div>;
                  })()}
                        </AnimatePresence>

                        {/* Delivery Day Countdown */}
                        <AnimatePresence data-id="element-324">
                          {deliveryDate && <motion.div initial={{
                    opacity: 0,
                    height: 0,
                    marginTop: 0
                  }} animate={{
                    opacity: 1,
                    height: 'auto',
                    marginTop: 12
                  }} exit={{
                    opacity: 0,
                    height: 0,
                    marginTop: 0
                  }} className="overflow-hidden" data-id="element-325">
                              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-green-500/10 border border-green-500/20 text-green-700 dark:text-green-400 text-sm font-bold" data-id="element-326">
                                <Clock className="w-4 h-4" data-id="element-327" />
                                {getDeliveryDayText()}
                              </div>
                            </motion.div>}
                        </AnimatePresence>

                        {/* Same day callout */}
                        <p className="text-sm text-stone-700 dark:text-stone-300 flex items-center gap-1.5 font-medium" data-id="element-328">
                          <Phone className="w-3.5 h-3.5 text-stone-500 dark:text-stone-400" data-id="element-329" />
                          Same day? Call{' '}
                          <a href="tel:206-619-3742" className="font-bold text-brand-orange hover:underline" data-id="element-330">
                            (206) 619-3742
                          </a>
                        </p>

                        {/* Dump Location (required) */}
                        <div data-id="element-331">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-1" data-id="element-332">
                            <MapPin className="w-5 h-5 mr-2 text-brand-muted" data-id="element-333" />
                            Dump Location
                            <span className="ml-1.5 text-red-500" aria-hidden="true" data-id="element-334">
                              *
                            </span>
                            <span className="sr-only" data-id="element-335">required</span>
                          </label>
                          <p className="text-xs text-brand-muted dark:text-stone-400 mb-3" data-id="element-336">
                            Where should the driver dump the load?
                          </p>
                          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-id="element-337">
                            {['Driveway', 'Street', 'Parking Pad', 'Backyard', 'Garage', 'Other'].map(opt => {
                      const isActive = dumpLocation === opt;
                      return <button key={opt} type="button" onClick={() => setDumpLocation(opt)} aria-pressed={isActive} className={`px-3 py-2.5 rounded-lg border text-sm font-semibold transition-all ${isActive ? 'border-brand-orange bg-brand-orange/10 text-brand-orange' : 'border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 text-brand-charcoal dark:text-stone-200 hover:border-stone-300 dark:hover:border-white/20'}`} data-id="element-338">
                                  {opt}
                                </button>;
                    })}
                          </div>
                        </div>

                        {/* Additional Instructions (optional) */}
                        <div data-id="element-339">
                          <label className="flex items-center text-base font-bold text-brand-charcoal dark:text-stone-100 mb-2" data-id="element-340">
                            <MessageSquare className="w-5 h-5 mr-2 text-brand-muted" data-id="element-341" />
                            Additional Notes
                            <span className="ml-1.5 text-xs font-medium text-stone-500 dark:text-stone-400" data-id="element-342">
                              (optional)
                            </span>
                          </label>
                          <textarea value={instructions} onChange={e => setInstructions(e.target.value)} placeholder="e.g. Dump on left side of driveway, gate code 1234..." rows={2} className={`${inputClass} resize-none`} data-id="element-343" />
                        </div>
                      </div>
                    </div>}

                  {/* Summary */}
                  {items.length > 0 && <div className={`px-8 pb-8 border-t border-white/20 dark:border-white/10 pt-8 bg-gradient-to-t from-white/50 to-transparent dark:from-white/5 dark:to-transparent ${showCalendar ? 'relative z-[50]' : ''}`} data-id="element-344">
                      <div className="space-y-3 mb-6" data-id="element-345">
                        <div className="flex justify-between items-end" data-id="element-346">
                          <span className="text-brand-muted dark:text-stone-300 font-bold text-base" data-id="element-347">
                            Materials ({materialYards} yards
                            {yardsBilledExtra > 0 && <span className="font-medium text-amber-600 dark:text-amber-400" data-id="element-348">
                                {' '}
                                · billed as {totalYards} yd (3 yd order min)
                              </span>}{' '}
                            · ≈{' '}
                            {items.reduce((sum, item) => sum + (item.requestedQuantity ?? item.quantity) * getDensity(item.product), 0).toFixed(1)}{' '}
                            tons
                            {deliveryCount > 1 ? ` · ${deliveryCount} loads` : ''}
                            )
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-349">
                            ${totalPrice.toFixed(2)}
                          </span>
                        </div>

                        {/* Promo Code */}
                        <div className="py-1" data-id="element-350">
                          {promo.code ? <div className="flex justify-between items-center bg-green-500/10 border border-green-500/30 rounded-lg px-3 py-2" data-id="element-351">
                              <div className="flex items-center gap-2" data-id="element-352">
                                <Check className="w-4 h-4 text-amber-500 dark:text-amber-400" data-id="element-353" />
                                <span className="text-sm font-bold text-amber-600 dark:text-amber-400" data-id="element-354">
                                  {promo.code} applied (10% off materials)
                                </span>
                                <button type="button" onClick={() => {
                        clearPromoCode();
                        setPromoInput('');
                        try {
                          sessionStorage.removeItem('promo_hint');
                        } catch {}
                      }} className="text-xs text-stone-500 hover:text-stone-700 dark:hover:text-stone-200 underline ml-1" data-id="element-355">
                                  Remove
                                </button>
                              </div>
                              <span className="font-extrabold text-base text-amber-600 dark:text-amber-400" data-id="element-356">
                                −${promo.discount.toFixed(2)}
                              </span>
                            </div> : !showPromoInput ? <button type="button" onClick={() => setShowPromoInput(true)} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors underline" data-id="element-357">
                              Have a promo code?
                            </button> : <div className="space-y-1.5" data-id="element-358">
                              <div className="flex gap-2" data-id="element-359">
                                <input type="text" value={promoInput} onChange={e => setPromoInput(e.target.value.toUpperCase())} onKeyDown={e => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          const ok = applyPromoCode(promoInput);
                          if (ok) {
                            try {
                              sessionStorage.removeItem('promo_hint');
                            } catch {}
                          }
                        }
                      }} placeholder="Enter code (e.g. FIRST10)" className="flex-1 px-3 py-2 bg-white/95 dark:bg-[#3a3938] border border-stone-200 dark:border-white/15 rounded-lg text-stone-900 dark:text-white placeholder-stone-400 text-sm font-bold uppercase tracking-wider focus:ring-2 focus:ring-brand-orange/50 focus:border-brand-orange/50 focus:outline-none" data-id="element-360" />
                                <button type="button" onClick={() => {
                        const ok = applyPromoCode(promoInput);
                        if (ok) {
                          try {
                            sessionStorage.removeItem('promo_hint');
                          } catch {}
                        }
                      }} className="px-4 py-2 bg-brand-charcoal dark:bg-white/10 hover:bg-brand-charcoal/90 dark:hover:bg-white/20 text-white rounded-lg font-bold text-sm border border-transparent dark:border-white/10 transition-colors" data-id="element-361">
                                  Apply
                                </button>
                              </div>
                              {promo.error && <p className="text-xs text-red-500 dark:text-red-400 font-medium px-1" data-id="element-362">
                                  {promo.error}
                                </p>}
                            </div>}
                        </div>

                        {deliveryCount > 1 && <div className="flex items-start gap-2 p-4 bg-brand-orange/10 border border-brand-orange/20 rounded-lg backdrop-blur-sm" data-id="element-363">
                            <Info className="w-5 h-5 text-brand-orange mt-0.5 shrink-0" data-id="element-364" />
                            <p className="text-sm text-stone-700 dark:text-stone-300 font-medium" data-id="element-365">
                              Your order requires {deliveryCount} separate
                              deliveries. Each delivery has its own delivery
                              fee.
                            </p>
                          </div>}

                        {/* Delivery Fee Row */}
                        <div className="flex justify-between items-center" data-id="element-366">
                          <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-367">
                            Delivery Fee
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-368">
                            {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-369" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange text-sm font-bold" data-id="element-370">
                                Custom Quote
                              </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500 font-extrabold" data-id="element-371">
                                FREE
                              </span> : deliveryFee > 0 ? <>
                                ${totalDeliveryFee.toFixed(2)}
                                {deliveryCount > 1 && <span className="text-sm text-stone-400 ml-1.5" data-id="element-372">
                                    ({deliveryCount} × ${deliveryFee.toFixed(2)}
                                    )
                                  </span>}
                              </> : <span className="text-stone-500 text-sm font-medium" data-id="element-373">
                                Enter address above
                              </span>}
                          </span>
                        </div>

                        {/* Saturday Surcharge Row */}
                        {saturdaySurcharge > 0 && <div className="flex justify-between items-center" data-id="element-374">
                            <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-375">
                              Saturday Delivery
                              <span className="text-xs font-medium text-stone-400 dark:text-stone-500 ml-1" data-id="element-376">
                                (weekend surcharge)
                              </span>
                            </span>
                            <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-377">
                              ${saturdaySurcharge.toFixed(2)}
                            </span>
                          </div>}

                        {/* Estimated Tax Row */}
                        <div className="flex justify-between items-center" data-id="element-378">
                          <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-379">
                            Est. Sales Tax{' '}
                            <span className="text-xs font-medium text-stone-400 dark:text-stone-500" data-id="element-380">
                              (10.1%)
                            </span>
                          </span>
                          <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-381">
                            ${estimatedTax.toFixed(2)}
                          </span>
                        </div>

                        <div className="flex justify-between items-end pt-4 border-t border-white/20 dark:border-white/10" data-id="element-382">
                          <span className="text-brand-charcoal dark:text-white font-extrabold text-xl" data-id="element-383">
                            Total Estimate
                          </span>
                          <span className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-384">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                      <div className="space-y-4" data-id="element-385">
                        <button type="submit" disabled={!isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !deliveryTime} className="w-full flex items-center justify-center bg-[#1DB954] hover:bg-[#17A347] text-white py-5 rounded-xl font-extrabold text-lg transition-all group shadow-[0_0_20px_rgba(29,185,84,0.3)] hover:shadow-[0_0_25px_rgba(29,185,84,0.5)] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none" data-id="element-386">
                          Proceed to Checkout
                          <ArrowRight className="w-6 h-6 ml-2 group-hover:translate-x-1 transition-transform" data-id="element-387" />
                        </button>
                        <div className="grid grid-cols-2 gap-4" data-id="element-388">
                          <button type="button" onClick={handleContinueShopping} className="w-full py-4 rounded-xl font-extrabold text-base text-brand-charcoal dark:text-white border border-white/30 dark:border-white/10 bg-white/50 dark:bg-white/5 backdrop-blur-sm hover:bg-white/80 dark:hover:bg-white/10 transition-colors" data-id="element-389">
                            Add More
                          </button>
                          <button type="button" onClick={() => {
                    const cartText = items.map(item => {
                      const bumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
                      const yardsLabel = bumped ? `${item.requestedQuantity} yds material (billed ${item.quantity})` : `${item.quantity} yds`;
                      return `${yardsLabel} - ${item.product} (${item.pricePerYard}/yd)`;
                    }).join('%0D%0A');
                    const yardsLine = yardsBilledExtra > 0 ? `Material Yards: ${materialYards}%0D%0ABilled Yards: ${totalYards} (3 yd order min)` : `Total Yards: ${totalYards}`;
                    const mailtoUrl = `mailto:?subject=253 Gravel - My Cart Summary&body=Here is my cart summary from 253 Gravel:%0D%0A%0D%0A${cartText}%0D%0A%0D%0A${yardsLine}%0D%0AMaterials Total: ${totalPrice.toFixed(2)}%0D%0A%0D%0AView materials at: https://253gravel.com`;
                    window.location.href = mailtoUrl;
                  }} className="w-full py-4 rounded-xl font-extrabold text-base text-brand-charcoal dark:text-white border border-white/30 dark:border-white/10 bg-white/50 dark:bg-white/5 backdrop-blur-sm hover:bg-white/80 dark:hover:bg-white/10 transition-colors flex items-center justify-center" data-id="element-390">
                            <Mail className="w-4 h-4 mr-2" data-id="element-391" />
                            Email Cart
                          </button>
                        </div>
                      </div>
                      <p className="text-sm text-brand-charcoal/70 dark:text-white/95 text-center mt-5 font-medium" data-id="element-392">
                        Need more than 12 yards or a custom amount?{' '}
                        <a href="tel:206-619-3742" className="text-brand-orange hover:underline font-bold" data-id="element-393">
                          Call us
                        </a>{' '}
                        or{' '}
                        <a href="/contact" className="text-brand-orange hover:underline font-bold" data-id="element-394">
                          contact us
                        </a>
                        .
                      </p>
                    </div>}
                </form>}
              {/* ===== CHECKOUT VIEW ===== */}
              {view === 'checkout' && <div className="flex-1 overflow-y-auto relative" data-id="element-395">
                  {/* Review header */}
                  <div className="sticky top-0 z-[60] bg-white/95 dark:bg-[#2a2a2a]/95 backdrop-blur-md border-b border-stone-200 dark:border-white/10 px-8 py-4 shadow-sm" data-id="element-396">
                    <div className="flex items-center gap-2" data-id="element-397">
                      <ClipboardCheck className="w-5 h-5 text-[#1DB954]" data-id="element-398" />
                      <h3 className="font-heading text-base font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-399">
                        Review your order
                      </h3>
                    </div>
                  </div>

                  <div className="p-8" data-id="element-400">
                    <form id="checkout-form" onSubmit={handleSubmitOrder} className="space-y-8" data-id="element-401">
                      {/* Order Summary */}
                      <div data-id="element-402">
                        <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight mb-5" data-id="element-403">
                          Order Summary
                        </h3>
                        <div className="space-y-3 bg-white/50 dark:bg-white/5 backdrop-blur-sm rounded-2xl border border-white/30 dark:border-white/10 p-5 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]" data-id="element-404">
                          {items.map(item => {
                      const materialQty = item.requestedQuantity ?? item.quantity;
                      const isBumped = item.requestedQuantity !== undefined && item.requestedQuantity < item.quantity;
                      return <div key={item.id} className="flex justify-between items-center text-base py-3 border-b border-white/20 dark:border-white/10 last:border-0" data-id="element-405">
                                <div data-id="element-406">
                                  <span className="font-bold text-brand-charcoal dark:text-stone-200" data-id="element-407">
                                    {item.product}
                                  </span>
                                  <span className="text-brand-muted dark:text-stone-400 ml-2 font-medium" data-id="element-408">
                                    × {materialQty} yds
                                    {isBumped && <span className="text-amber-600 dark:text-amber-400" data-id="element-409">
                                        {' '}
                                        (billed {item.quantity} yd · 3 yd min)
                                      </span>}{' '}
                                    · ≈{' '}
                                    {(materialQty * getDensity(item.product)).toFixed(1)}{' '}
                                    tons
                                  </span>
                                </div>
                                <span className="font-extrabold text-brand-charcoal dark:text-white" data-id="element-410">
                                  $
                                  {(() => {
                            const surcharge = item.quantity < 10 ? 1 + (10 - item.quantity) * 0.1 : 1;
                            return (item.quantity * Math.ceil(item.pricePerYard * surcharge)).toFixed(2);
                          })()}
                                </span>
                              </div>;
                    })}
                          <div className="space-y-3 pt-3" data-id="element-411">
                            <div className="flex justify-between items-center" data-id="element-412">
                              <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-413">
                                Subtotal
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-414">
                                ${totalPrice.toFixed(2)}
                              </span>
                            </div>

                            {/* Promo Discount Row */}
                            {promo.code && promo.discount > 0 && <div className="flex justify-between items-center" data-id="element-415">
                                <span className="text-base font-bold text-green-600 dark:text-green-400" data-id="element-416">
                                  Promo {promo.code} (10% off materials)
                                </span>
                                <span className="font-extrabold text-lg text-green-600 dark:text-green-400" data-id="element-417">
                                  −${promo.discount.toFixed(2)}
                                </span>
                              </div>}

                            {/* Delivery Fee Row */}
                            <div className="flex justify-between items-center" data-id="element-418">
                              <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-419">
                                Delivery Fee
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-420">
                                {isCalculatingDelivery ? <Loader2 className="w-5 h-5 animate-spin text-brand-orange" data-id="element-421" /> : deliveryZone === null && addressInput ? <span className="text-brand-orange text-sm font-bold" data-id="element-422">
                                    Custom Quote
                                  </span> : deliveryFee === 0 && addressInput ? <span className="text-green-500 font-extrabold" data-id="element-423">
                                    FREE
                                  </span> : deliveryFee > 0 ? <>
                                    {deliveryCount > 1 && <span className="text-sm text-stone-400 mr-1.5" data-id="element-424">
                                        ({deliveryCount} × $
                                        {deliveryFee.toFixed(2)})
                                      </span>}
                                    ${totalDeliveryFee.toFixed(2)}
                                  </> : <span className="text-stone-500 text-sm font-medium" data-id="element-425">
                                    Based on address
                                  </span>}
                              </span>
                            </div>

                            {/* Saturday Surcharge Row */}
                            {saturdaySurcharge > 0 && <div className="flex justify-between items-center" data-id="element-426">
                                <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-427">
                                  Saturday Delivery
                                  <span className="text-xs font-medium text-stone-400 dark:text-stone-500 ml-1" data-id="element-428">
                                    (weekend surcharge)
                                  </span>
                                </span>
                                <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-429">
                                  ${saturdaySurcharge.toFixed(2)}
                                </span>
                              </div>}

                            {/* Estimated Tax Row */}
                            <div className="flex justify-between items-center" data-id="element-430">
                              <span className="text-base font-bold text-brand-muted dark:text-stone-400" data-id="element-431">
                                Est. Sales Tax{' '}
                                <span className="text-xs font-medium text-stone-400 dark:text-stone-500" data-id="element-432">
                                  (10.1%)
                                </span>
                              </span>
                              <span className="font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-433">
                                ${estimatedTax.toFixed(2)}
                              </span>
                            </div>

                            <div className="flex justify-between items-center pt-4 border-t border-white/20 dark:border-white/10" data-id="element-434">
                              <span className="font-extrabold text-xl text-brand-charcoal dark:text-white" data-id="element-435">
                                Total
                              </span>
                              <span className="font-heading text-3xl font-extrabold text-brand-charcoal dark:text-white" data-id="element-436">
                                ${finalTotal.toFixed(2)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Delivery Details Summary (Read-Only) */}
                      <div data-id="element-437">
                        <div className="flex items-center justify-between mb-4" data-id="element-438">
                          <h3 className="font-heading text-2xl font-extrabold text-brand-charcoal dark:text-white tracking-tight" data-id="element-439">
                            Delivery Details
                          </h3>
                          <button type="button" onClick={handleBackToCart} className="text-sm font-bold text-brand-orange hover:text-brand-orangeHover transition-colors" data-id="element-440">
                            Edit
                          </button>
                        </div>
                        <div className="bg-white/50 dark:bg-white/5 backdrop-blur-sm border border-white/30 dark:border-white/10 rounded-xl p-5 space-y-4 shadow-[inset_0_1px_1px_rgba(255,255,255,0.1)]" data-id="element-441">
                          <div className="flex items-start gap-3" data-id="element-442">
                            <MapPin className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-443" />
                            <div data-id="element-444">
                              <p className="text-base text-brand-charcoal dark:text-white" data-id="element-445">
                                {addressInput}
                              </p>
                              {deliveryZone !== null ? <p className="text-sm text-green-600 dark:text-green-400 mt-0.5 font-medium" data-id="element-446">
                                  Zone {deliveryZone}
                                </p> : <p className="text-sm text-brand-orange mt-0.5 font-medium" data-id="element-447">
                                  Outside standard zones (Custom Quote)
                                </p>}
                            </div>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-448">
                            <Phone className="w-5 h-5 text-brand-muted shrink-0" data-id="element-449" />
                            <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-450">
                              {phone}
                            </p>
                          </div>
                          <div className="flex items-center gap-3" data-id="element-451">
                            <CalendarDays className="w-5 h-5 text-brand-muted shrink-0" data-id="element-452" />
                            <p className="text-base text-brand-charcoal dark:text-white font-medium" data-id="element-453">
                              {deliveryDate ? new Date(deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                          weekday: 'short',
                          month: 'short',
                          day: 'numeric'
                        }) : 'No date selected'}
                              {deliveryTime && <>
                                  <span className="text-brand-muted dark:text-stone-400 mx-2" data-id="element-454">
                                    •
                                  </span>
                                  <span className="capitalize" data-id="element-455">
                                    {deliveryTime}
                                  </span>
                                </>}
                            </p>
                          </div>
                          {instructions && <div className="flex items-start gap-3 pt-3 border-t border-white/20 dark:border-white/10" data-id="element-456">
                              <MessageSquare className="w-5 h-5 text-brand-muted mt-0.5 shrink-0" data-id="element-457" />
                              <p className="text-base text-brand-charcoal dark:text-stone-300 italic font-medium" data-id="element-458">
                                "{instructions}"
                              </p>
                            </div>}
                        </div>
                      </div>

                      {/* Payment Method + Place Order */}
                      <div data-id="element-459">
                        <div className="flex items-baseline justify-between mb-3" data-id="element-460">
                          <h3 className="font-heading text-lg font-bold text-brand-charcoal dark:text-white tracking-tight" data-id="element-461">
                            Pay on Delivery
                          </h3>
                          <span className="text-xs text-stone-500 dark:text-stone-400 font-medium" data-id="element-462">
                            Online payment coming soon
                          </span>
                        </div>
                        <p className="text-sm text-brand-muted dark:text-stone-400 mb-3" data-id="element-463">
                          Choose how you'll pay your driver, then place your
                          order
                        </p>
                        <div className="grid grid-cols-2 gap-2 mb-3" data-id="element-464">
                          {([{
                      id: 'cash',
                      label: 'Cash',
                      icon: Banknote
                    }, {
                      id: 'card',
                      label: 'Card',
                      icon: CreditCard
                    }] as const).map(opt => {
                      const isActive = payOnDeliverySubMethod === opt.id;
                      const Icon = opt.icon;
                      return <button key={opt.id} type="button" onClick={() => {
                        setPaymentMethod('delivery');
                        setPayOnDeliverySubMethod(opt.id);
                      }} className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-semibold transition-all ${isActive ? 'border-brand-orange bg-brand-orange/10 text-brand-orange' : 'border-stone-200 dark:border-white/10 bg-white/50 dark:bg-white/5 text-brand-charcoal dark:text-stone-200 hover:border-stone-300 dark:hover:border-white/20'}`} aria-pressed={isActive} data-id="element-465">
                                <Icon className="w-4 h-4 shrink-0" data-id="element-466" />
                                <span data-id="element-467">{opt.label}</span>
                              </button>;
                    })}
                        </div>
                        {(() => {
                    const placeOrderDisabled = !isAddressValid || !isNameValid || !isPhoneValid || !isEmailValid || !isDateValid || !dumpLocation || isProcessingPayment;
                    const placeOrderReady = !placeOrderDisabled;
                    return <motion.button type="submit" disabled={placeOrderDisabled} animate={placeOrderReady ? {
                      boxShadow: ['0 0 0 0 rgba(29,185,84,0.55), 0 4px 14px rgba(29,185,84,0.35)', '0 0 0 10px rgba(29,185,84,0)', '0 0 0 0 rgba(29,185,84,0)']
                    } : {
                      boxShadow: '0 1px 2px rgba(29,185,84,0.15)'
                    }} transition={placeOrderReady ? {
                      duration: 1.8,
                      repeat: Infinity,
                      ease: 'easeOut'
                    } : {
                      duration: 0.2
                    }} whileTap={placeOrderReady ? {
                      scale: 0.98
                    } : undefined} className="relative w-full flex items-center justify-center gap-2 px-4 py-7 rounded-lg text-base font-bold transition-colors bg-[#1DB954] hover:bg-[#17A347] text-white disabled:opacity-50 disabled:cursor-not-allowed mb-5" data-id="element-468">
                              {isProcessingPayment ? <>
                                  <Loader2 className="w-5 h-5 animate-spin" data-id="element-469" />
                                  <span data-id="element-470">Processing</span>
                                </> : <>
                                  <Check className="w-5 h-5" strokeWidth={3} data-id="element-471" />
                                  <span data-id="element-472">Place Order</span>
                                </>}
                            </motion.button>;
                  })()}
                      </div>

                      {/* SMS Opt-in */}
                      <div className="p-6 border-b border-stone-100 dark:border-stone-800" data-id="element-473">
                        <label className="flex items-start gap-3 cursor-pointer group" data-id="element-474">
                          <div className="relative flex items-center justify-center mt-0.5" data-id="element-475">
                            <input type="checkbox" checked={smsOptIn} onChange={e => setSmsOptIn(e.target.checked)} className="peer sr-only" data-id="element-476" />
                            <div className="w-5 h-5 border-2 border-stone-300 dark:border-stone-600 rounded transition-colors peer-checked:bg-brand-orange peer-checked:border-brand-orange group-hover:border-brand-orange" data-id="element-477"></div>
                            <Check className="absolute w-3.5 h-3.5 text-white opacity-0 peer-checked:opacity-100 transition-opacity pointer-events-none" data-id="element-478" />
                          </div>
                          <div data-id="element-479">
                            <span className="text-sm font-medium text-stone-900 dark:text-white block mb-0.5" data-id="element-480">
                              Send me SMS updates about this delivery
                            </span>
                            <span className="text-xs text-stone-500 dark:text-stone-400" data-id="element-481">
                              Get notified when your driver is assigned, en
                              route, and delivered.
                            </span>
                          </div>
                        </label>
                      </div>

                      {/* Total reminder */}
                      <div className="px-6 pb-6" data-id="element-482">
                        <div className="flex items-center justify-between text-sm" data-id="element-483">
                          <span className="text-brand-muted dark:text-stone-400 font-medium" data-id="element-484">
                            Order total
                          </span>
                          <span className="font-heading font-extrabold text-lg text-brand-charcoal dark:text-white" data-id="element-485">
                            ${finalTotal.toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </form>
                  </div>
                </div>}
              {/* ===== SUCCESS VIEW ===== */}
              {view === 'success' && orderSummaryData && <div className="flex-1 overflow-y-auto p-8" data-id="element-486">
                  <div className="flex flex-col items-center text-center mb-8" data-id="element-487">
                    <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 200,
                damping: 20
              }} className="w-24 h-24 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mb-6 relative" data-id="element-488">
                      <CheckCircle2 className="w-12 h-12 text-green-600 dark:text-green-400" data-id="element-489" />
                      <motion.div initial={{
                  scale: 1,
                  opacity: 0.6
                }} animate={{
                  scale: 1.6,
                  opacity: 0
                }} transition={{
                  duration: 1.4,
                  repeat: Infinity,
                  ease: 'easeOut'
                }} className="absolute inset-0 rounded-full border-2 border-green-500" data-id="element-490" />
                    </motion.div>
                    <h3 className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-charcoal dark:text-white mb-3" data-id="element-491">
                      Order Received!
                    </h3>
                    <p className="text-lg text-brand-muted dark:text-stone-300 mb-4 max-w-md" data-id="element-492">
                      Thank you, {orderSummaryData.name}. We've received your
                      order request for{' '}
                      {orderSummaryData.materialYards ?? orderSummaryData.totalYards}{' '}
                      yards of material
                      {orderSummaryData.yardsBilledExtra > 0 && <span className="text-amber-600 dark:text-amber-400" data-id="element-493">
                          {' '}
                          (billed as {orderSummaryData.totalYards} yd — 3 yd
                          order min)
                        </span>}
                      .
                    </p>
                    <div className="inline-flex items-center gap-2 bg-brand-orange/10 border border-brand-orange/20 px-4 py-2 rounded-full" data-id="element-494">
                      <span className="text-xs font-bold text-brand-muted dark:text-stone-400 uppercase tracking-wider" data-id="element-495">
                        Confirmation #
                      </span>
                      <span className="font-mono font-extrabold text-brand-orange" data-id="element-496">
                        {orderSummaryData.orderNumber}
                      </span>
                    </div>
                  </div>

                  {/* What happens next — stepped timeline */}
                  <div className="bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-2xl p-6 mb-6 text-left" data-id="element-497">
                    <h4 className="font-bold text-brand-charcoal dark:text-white mb-5 flex items-center gap-2" data-id="element-498">
                      <MessageSquare className="w-5 h-5 text-brand-orange" data-id="element-499" />
                      What happens next?
                    </h4>
                    <div className="space-y-4" data-id="element-500">
                      {[{
                  icon: Send,
                  title: 'SMS confirmation',
                  desc: <>
                              Text confirmation arriving at{' '}
                              <strong className="text-brand-charcoal dark:text-white" data-id="element-501">
                                {formatPhone(orderSummaryData.phone)}
                              </strong>{' '}
                              within a few minutes.
                            </>,
                  active: true
                }, {
                  icon: ClipboardCheck,
                  title: 'Dispatch review',
                  desc: 'Our team verifies your address, materials, and delivery window — usually within 1 business hour.'
                }, {
                  icon: Truck,
                  title: 'Driver dispatched',
                  desc: 'On delivery day, we text you a live tracking link the moment your driver is en route.'
                }].map((step, i) => {
                  const Icon = step.icon;
                  return <div key={i} className="flex gap-4" data-id="element-502">
                            <div className="flex flex-col items-center" data-id="element-503">
                              <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${step.active ? 'bg-[#1DB954] text-white shadow-[0_0_12px_rgba(29,185,84,0.4)]' : 'bg-stone-200 dark:bg-white/10 text-stone-500 dark:text-stone-400'}`} data-id="element-504">
                                <Icon className="w-4 h-4" data-id="element-505" />
                              </div>
                              {i < 2 && <div className="w-px flex-1 bg-stone-200 dark:bg-white/10 my-1 min-h-[18px]" data-id="element-506" />}
                            </div>
                            <div className="pb-1 flex-1" data-id="element-507">
                              <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-508">
                                {step.title}
                              </p>
                              <p className="text-sm text-brand-muted dark:text-stone-400 mt-0.5 leading-relaxed" data-id="element-509">
                                {step.desc}
                              </p>
                            </div>
                          </div>;
                })}
                    </div>
                  </div>

                  {/* Order details card */}
                  <div className="bg-stone-50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-2xl p-6 mb-6 text-left" data-id="element-510">
                    <h4 className="font-bold text-brand-charcoal dark:text-white mb-4" data-id="element-511">
                      Order Details
                    </h4>
                    <div className="space-y-3 mb-4" data-id="element-512">
                      {orderSummaryData.items.map((item: any, i: number) => <div key={i} className="flex justify-between text-sm font-medium" data-id="element-513">
                          <span className="text-brand-charcoal dark:text-stone-200" data-id="element-514">
                            {item.quantity} yds — {item.product}
                          </span>
                        </div>)}
                    </div>
                    <div className="space-y-2 text-sm font-medium border-t border-stone-200 dark:border-white/10 pt-4 mb-4" data-id="element-515">
                      <div className="flex justify-between" data-id="element-516">
                        <span className="text-brand-muted dark:text-stone-400" data-id="element-517">
                          Materials Total
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-518">
                          ${orderSummaryData.totalPrice.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between" data-id="element-519">
                        <span className="text-brand-muted dark:text-stone-400" data-id="element-520">
                          Delivery Fee
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-521">
                          {orderSummaryData.deliveryFeeLabel}
                        </span>
                      </div>
                      {orderSummaryData.saturdaySurcharge > 0 && <div className="flex justify-between" data-id="element-522">
                          <span className="text-brand-muted dark:text-stone-400" data-id="element-523">
                            Saturday Delivery
                          </span>
                          <span className="text-brand-charcoal dark:text-white" data-id="element-524">
                            ${orderSummaryData.saturdaySurcharge.toFixed(2)}
                          </span>
                        </div>}
                      <div className="flex justify-between" data-id="element-525">
                        <span className="text-brand-muted dark:text-stone-400" data-id="element-526">
                          Est. Sales Tax (10.1%)
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-527">
                          ${orderSummaryData.estimatedTax.toFixed(2)}
                        </span>
                      </div>
                      <div className="flex justify-between text-base font-bold pt-2 border-t border-stone-200 dark:border-white/10" data-id="element-528">
                        <span className="text-brand-charcoal dark:text-white" data-id="element-529">
                          Estimated Total
                        </span>
                        <span className="text-brand-charcoal dark:text-white" data-id="element-530">
                          ${orderSummaryData.finalTotal.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    <div className="space-y-3 text-sm font-medium border-t border-stone-200 dark:border-white/10 pt-4" data-id="element-531">
                      <div className="flex items-start gap-3" data-id="element-532">
                        <MapPin className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-533" />
                        <span className="text-brand-charcoal dark:text-stone-200" data-id="element-534">
                          {orderSummaryData.addressInput}
                        </span>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-535">
                        <CalendarDays className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-536" />
                        <span className="text-brand-charcoal dark:text-stone-200" data-id="element-537">
                          {new Date(orderSummaryData.deliveryDate + 'T12:00:00').toLocaleDateString('en-US', {
                      weekday: 'short',
                      month: 'short',
                      day: 'numeric'
                    })}{' '}
                          •{' '}
                          <span className="capitalize" data-id="element-538">
                            {orderSummaryData.timeLabel}
                          </span>
                        </span>
                      </div>
                      <div className="flex items-start gap-3" data-id="element-539">
                        <Phone className="w-4 h-4 text-brand-muted mt-0.5 shrink-0" data-id="element-540" />
                        <span className="text-brand-charcoal dark:text-stone-200" data-id="element-541">
                          {formatPhone(orderSummaryData.phone)}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Prep tip link */}
                  <button type="button" onClick={() => {
              handleCloseSuccess();
              navigate('/delivery-prep');
            }} className="w-full flex items-center justify-between bg-white/50 dark:bg-white/5 border border-stone-200 dark:border-white/10 rounded-xl p-4 mb-3 hover:bg-white dark:hover:bg-white/10 transition-colors text-left" data-id="element-542">
                    <div className="flex items-center gap-3" data-id="element-543">
                      <Info className="w-5 h-5 text-brand-orange shrink-0" data-id="element-544" />
                      <div data-id="element-545">
                        <p className="font-bold text-sm text-brand-charcoal dark:text-white" data-id="element-546">
                          How to prep for delivery
                        </p>
                        <p className="text-xs text-brand-muted dark:text-stone-400" data-id="element-547">
                          Driveway clearance, dump zone, what to expect
                        </p>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-brand-muted" data-id="element-548" />
                  </button>

                  {/* Action button */}
                  <button onClick={handleCloseSuccess} className="w-full bg-brand-charcoal dark:bg-white/10 hover:bg-brand-charcoal/90 dark:hover:bg-white/20 text-white py-4 rounded-xl font-extrabold text-lg transition-colors border border-transparent dark:border-white/10 backdrop-blur-sm mt-4" data-id="element-549">
                    Done
                  </button>
                </div>}
            </motion.div>
          </div>
        </>}
    </AnimatePresence>;
}